// Paytm Checkout JS integration helper for Web
// Modeled after the proven implementation in web-music-recommendation
import { API_BASE_URL } from "./config";

// --- Paytm hosts ---
const STAGING_HOST = "https://securestage.paytmpayments.com";
const PROD_HOST = "https://secure.paytmpayments.com";

/**
 * Detect the correct Paytm host from callbackUrl, API base URL, or hostname.
 */
export function resolvePaytmHost(callbackUrl) {
  // 1. Explicit env check if provided
  if (typeof process !== "undefined" && process.env?.REACT_APP_PAYTM_ENV) {
    if (process.env.REACT_APP_PAYTM_ENV === "production") return PROD_HOST;
    if (process.env.REACT_APP_PAYTM_ENV === "staging") return STAGING_HOST;
  }

  // 2. Infer from callback URL returned by backend
  if (callbackUrl) {
    const cb = String(callbackUrl).toLowerCase();
    if (
      cb.includes("securegw-stage.paytm.in") ||
      cb.includes("securestage.paytmpayments.com") ||
      cb.includes("stage") ||
      cb.includes("dev")
    ) {
      return STAGING_HOST;
    }
    if (
      cb.includes("securegw.paytm.in") ||
      cb.includes("secure.paytmpayments.com")
    ) {
      return PROD_HOST;
    }
  }

  // 3. Infer from API base URL (e.g. dev-api.tringbox.com or test-api.tringbox.com)
  const api = String(API_BASE_URL || "").toLowerCase();
  if (/test-api|staging|stage\.|dev-api|localhost|127\.0\.0\.1/.test(api)) {
    return STAGING_HOST;
  }

  // 4. Infer from client browser hostname
  if (typeof window !== "undefined") {
    const host = window.location.hostname.toLowerCase();
    if (host === "localhost" || host.includes("127.0.0.1") || host.includes("dev")) {
      return STAGING_HOST;
    }
  }

  return PROD_HOST;
}

export const getPaytmScriptUrl = (mid, callbackUrl) => {
  const host = resolvePaytmHost(callbackUrl);
  return `${host}/merchantpgpui/checkoutjs/merchants/${mid}.js`;
};

/**
 * Dynamically inject the Paytm CheckoutJS bootstrap script for a given MID and host.
 * Drops cached scripts if loaded for a different host or environment.
 */
export function loadPaytmScript(mid, hostOrCallback) {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Not in browser"));
      return;
    }

    const host =
      hostOrCallback && hostOrCallback.startsWith("http")
        ? hostOrCallback.includes("merchantpgpui") || hostOrCallback.includes("paytm")
          ? hostOrCallback.startsWith("https://secure")
            ? hostOrCallback
            : resolvePaytmHost(hostOrCallback)
          : resolvePaytmHost(hostOrCallback)
        : resolvePaytmHost();

    // Drop cached script if it was loaded for a different host
    document
      .querySelectorAll(`script[data-paytm-mid="${mid}"]`)
      .forEach((el) => {
        if (el.dataset.paytmHost !== host) {
          console.log(`[Paytm] Removing script for old host ${el.dataset.paytmHost}, switching to ${host}`);
          el.remove();
          if (window.Paytm) delete window.Paytm;
        }
      });

    // Already loaded for same mid + host
    const existing = document.querySelector(
      `script[data-paytm-mid="${mid}"][data-paytm-host="${host}"]`
    );
    if (existing) {
      if (existing.dataset.loaded === "true") {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Paytm script failed to load"))
      );
      return;
    }

    const script = document.createElement("script");
    script.type = "application/javascript";
    script.crossOrigin = "anonymous";
    script.src = `${host}/merchantpgpui/checkoutjs/merchants/${mid}.js`;
    script.dataset.paytmMid = mid;
    script.dataset.paytmHost = host;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => {
      reject(new Error(`Paytm script failed to load from ${script.src}`));
    };
    document.head.appendChild(script);
  });
}

/**
 * Wait until Paytm CheckoutJS is initialized.
 */
export function waitForCheckoutJSReady(timeoutMs = 15000) {
  return new Promise((resolve, reject) => {
    const start = Date.now();

    const check = () => {
      const cjs = window.Paytm?.CheckoutJS;

      if (cjs && typeof cjs.onLoad === "function") {
        try {
          cjs.onLoad(() => resolve(cjs));
          return;
        } catch (e) {
          console.warn("[Paytm] onLoad error:", e);
        }
      }

      if (cjs && typeof cjs.init === "function") {
        resolve(cjs);
        return;
      }

      if (Date.now() - start > timeoutMs) {
        reject(
          new Error(`Paytm CheckoutJS did not initialize within ${timeoutMs}ms`)
        );
        return;
      }

      setTimeout(check, 100);
    };

    check();
  });
}

/**
 * Format amount strictly to 2 decimal places string — Paytm requirement.
 * If raw amount is an integer >= 100 (in paise), convert to rupees first.
 */
export function normalizeAmount(raw) {
  const num = typeof raw === "string" ? parseFloat(raw) : raw;
  if (isNaN(num)) return "1.00";
  if (Number.isInteger(num) && num >= 100) {
    return (num / 100).toFixed(2);
  }
  return num.toFixed(2);
}

// Module-level flag — true after a checkout has been opened once.
// Needed because Paytm's internal state breaks on re-init after close.
let hasOpenedCheckout = false;

/**
 * Cleanup any existing Paytm modal, globals, and script tags to allow clean retries.
 */
export function cleanupPaytmState(mid) {
  if (typeof window === "undefined") return;

  const cjs = window.Paytm?.CheckoutJS;
  if (cjs && typeof cjs.close === "function") {
    try {
      cjs.close();
    } catch {
      // ignore
    }
  }

  if (window.Paytm) {
    delete window.Paytm;
  }

  if (mid) {
    document
      .querySelectorAll(`script[data-paytm-mid="${mid}"]`)
      .forEach((el) => el.remove());
  }

  // Remove any leftover Paytm DOM nodes (iframes, modal containers)
  document
    .querySelectorAll(
      '#paytm-checkoutjs, .paytm-checkout-modal, iframe[src*="paytm"], iframe[id*="paytm"]'
    )
    .forEach((el) => el.remove());
}

/**
 * Open the Paytm Checkout modal.
 *
 * @param {Object} options
 * @param {string} options.orderId - Unique order ID from backend
 * @param {string} options.txnToken - TXN_TOKEN from backend
 * @param {number|string} options.amount - Amount in paise or rupees
 * @param {string} options.mid - Merchant ID
 * @param {string} [options.callbackUrl] - Callback URL returned by backend
 * @param {string} [options.mobileNumber] - Optional 10-digit mobile
 * @param {string} [options.name] - Customer name
 * @param {function} [options.onSuccess] - Callback when TXN_SUCCESS
 * @param {function} [options.onError] - Callback when transaction fails or errors
 * @param {function} [options.onClose] - Callback when user cancels/closes modal
 */
export async function openPaytmCheckout({
  orderId,
  txnToken,
  amount,
  mid,
  callbackUrl,
  mobileNumber,
  name,
  onSuccess,
  onError,
  onClose,
}) {
  if (typeof window === "undefined") {
    throw new Error("Not in browser");
  }

  if (!orderId || !txnToken || !mid) {
    const err = new Error("Missing required Paytm checkout parameters (orderId, txnToken, or mid)");
    if (onError) onError(err);
    throw err;
  }

  const host = resolvePaytmHost(callbackUrl);
  console.log(`[Paytm] Initiating checkout on host: ${host} for order: ${orderId}`);

  // On retry: clean up previous Paytm state completely
  if (hasOpenedCheckout) {
    cleanupPaytmState(mid);
  }

  try {
    // 1. Inject the merchant script
    await loadPaytmScript(mid, host);

    // 2. Wait for CheckoutJS SDK to be ready
    await waitForCheckoutJSReady();

    const cjs = window.Paytm?.CheckoutJS;
    if (!cjs || typeof cjs.init !== "function") {
      throw new Error("Paytm CheckoutJS is not available. Verify the MID is valid for the current environment.");
    }

    const rawMobile =
      mobileNumber ||
      (typeof window !== "undefined"
        ? localStorage.getItem("number") ||
          localStorage.getItem("phone") ||
          localStorage.getItem("phoneNumber") ||
          ""
        : "");

    const userDetail = {};
    const userInfo = {};
    if (rawMobile) {
      const digits = String(rawMobile).replace(/\D/g, "");
      let normalized = "";
      if (digits.length === 10) normalized = digits;
      else if (digits.length === 12 && digits.startsWith("91")) normalized = digits.slice(2);
      else if (digits.length === 11 && digits.startsWith("0")) normalized = digits.slice(1);
      else if (digits.length > 10) normalized = digits.slice(-10);

      if (normalized && normalized.length === 10) {
        userDetail.mobileNumber = normalized;
        userInfo.mobile = normalized;
      }
    }
    if (name) {
      userDetail.name = name;
      userInfo.name = name;
    }

    const formattedAmount = normalizeAmount(amount);

    const config = {
      root: "",
      flow: "DEFAULT",
      data: {
        orderId: orderId,
        token: txnToken,
        tokenType: "TXN_TOKEN",
        amount: formattedAmount,
        ...(Object.keys(userDetail).length > 0 ? { userDetail } : {}),
        ...(Object.keys(userInfo).length > 0 ? { userInfo } : {}),
      },
      merchant: {
        mid: mid,
        redirect: false,
      },
      payMode: {
        filter: [],
        order: ["UPI"],
      },
      handler: {
        notifyMerchant: (eventName, data) => {
          console.log("[Paytm] notifyMerchant:", eventName, data);
          if (eventName === "APP_CLOSED" || eventName === "POPUP_CLOSED") {
            if (onClose) onClose();
          } else if (eventName === "SESSION_EXPIRED") {
            const err = new Error("Paytm session expired. Please retry.");
            if (onError) onError(err, data);
          }
        },
        transactionStatus: (response) => {
          console.log("[Paytm] transactionStatus:", response);
          // Close the modal — Paytm does NOT auto-close after payment
          try {
            if (typeof cjs.close === "function") cjs.close();
          } catch {
            // ignore
          }

          if (
            response?.STATUS === "TXN_SUCCESS" ||
            response?.status === "TXN_SUCCESS" ||
            response?.RESPCODE === "01"
          ) {
            if (onSuccess) onSuccess(response);
          } else {
            const errMsg = response?.RESPMSG || response?.responseMessage || "Payment was not completed";
            if (onError) onError(new Error(errMsg), response);
          }
        },
      },
    };

    console.log("[Paytm] init config:", {
      orderId,
      amount: formattedAmount,
      host,
      mid,
    });

    await cjs.init(config);
    cjs.invoke();
    hasOpenedCheckout = true;
  } catch (err) {
    console.error("[Paytm] Checkout error:", err);
    if (onError) onError(err);
  }
}
